import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { fetchCompaniesService} from '../services/modules/idp/company';
import { addUserService,fetchRolesService} from 'services/modules/idp/user';

import {
  Autocomplete,
  Card,
  CardHeader,
  Divider,
  Button,
  CardContent,
  Grid,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";
import { useNotification } from 'config/Notification';

const AddUser = () => {
  // Redux dispatch
  const dispatch = useDispatch();
  
  // React router
  const navigate = useNavigate();
  
  // Notification
  const { openNotification } = useNotification();
  
  // useParams hook
  const { companyId } = useParams();

  // State variables
  const [phone, setPhone] = useState("91");
  const [selectedCompanyId, setSelectedCompanyId] = useState(companyId);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [errorMessage, setErrorMessage] = useState("");
  const [createdAt, setCreatedAt] = useState('');
  const [open, setOpen] = useState(false);
  const [loading,setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    mobile_code: phone,
    mobile_digits: '',
    email: '',
    mobile: {
      code: phone,
      digits: ''
    },
    company: "",
    roles: ""
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    username: '',
    mobile_digits: '',
    mobile_code: '91',
    email: '',
  });

  // Redux selectors
  const companyNamesContainer = useSelector((state) => state.company.companies);
  const rolesDataContainer = useSelector((state) => state.user.roles);

  // This method re-renders the add user component again with empty form data
  const handleAddMoreUsers = () => {
    setOpen(false);
    navigate(0);
  };
  // This method handles navigation to users component
  const handleBackToAllUsers = () => {
    setOpen(false); 
    navigate(-1);
  };

  // useEffect that sets the selected role to the first role
  useEffect(() => {
    if(rolesDataContainer && rolesDataContainer.data){
      const roleId = rolesDataContainer.data[0].id;
      setSelectedRoleId(roleId);
      setFormData((prevData) => ({
        ...prevData,
        roles: roleId,
      }));
    }
  }, [rolesDataContainer]);

  // useEffect for fetching roles and companies for dropdown
  useEffect(() => {
    const params = {
      limit: "null",
      flag:"true"
    };
    dispatch(fetchCompaniesService({params}));
    if (selectedCompanyId) {
      setFormData((prevData) => ({
        ...prevData,
        company: selectedCompanyId,
      }));
      dispatch(fetchRolesService({ params: { company: selectedCompanyId }}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  // Handles the submission of the user data
  const handleAddUser = async (event) => {
    event.preventDefault();
    setLoading(true);
    const emptyFields = Object.entries(formData)
      .filter(([name, value]) => value === '')
      .map(([name]) => name);

    if (emptyFields.length > 0) {
      const fieldNames = emptyFields.join(', ');
      setLoading(false);
      setErrorMessage(`Please fill in the required fields: ${fieldNames}`);
      return;
    } else {
      setErrorMessage('')
    }
    let updatedFormData = { ...formData };
    
    updatedFormData.mobile = {
      code: phone ? phone : formData.mobile_code,
      digits: formData.mobile_digits,
    };
    delete updatedFormData.mobile_code;
    delete updatedFormData.mobile_digits;
    updatedFormData.company = selectedCompanyId;
    updatedFormData.roles = [selectedRoleId];

    try {
      if(updatedFormData.username.match(/[A-Z]/g) && updatedFormData.username.match(/[A-Z]/g).length > 0){
        openNotification('Username should be in lower case only','error');
        return;
      }
      const response = await addUserService(updatedFormData);
      if (response.status === 200 || response.status === 201) {
        setErrorMessage('')
        setOpen(true);
        const currentDate = new Date();
        const formattedDate = currentDate.toLocaleString(); 
        setCreatedAt(formattedDate);
        setFormData({ name: '',username: '',mobile_digits: '',mobile_code: '91',email: '',});
      } else{
        setErrorMessage(response?.response?.data?.message);
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.message) {
        setErrorMessage(error.response.data.message);
        openNotification('error while creating user', 'error')
      } else {
        setErrorMessage('An error occurred while processing your request.');
      }
    }
    finally{
      setLoading(false);
    }
  };
  // Handles changes in form inputs
  const handleInputChange = (event) => {
    const { name, value } = event.target;
    let trimmedValue;
    if (name === "name"){
      trimmedValue = value;
    } else {
      trimmedValue = value.trim();
    }
    let errorMessage = '';

    if (trimmedValue === '') {
      errorMessage = 'Field is required';
    } else if (name === 'mobile_digits' && !/^\d+$/.test(trimmedValue)) {
      errorMessage = 'Only digits';
    } else if (name === 'email' && !/^\S+@\S+\.\S+$/.test(value))  {
      errorMessage = 'Invalid email';
    }else if(name ==='username' && !/^[^A-Z]*$/.test(value)){
      errorMessage = 'Username must be in lowercase';
    }

    const newErrors = {
      ...formErrors,
      [name]: errorMessage,
    };
    setFormErrors(newErrors);

    setFormData((prevData) => ({
      ...prevData,
      [name]: name === 'mobile_code' ? phone.slice(0, 3) : trimmedValue,
    }));
  };

  // This method displays the form error if the form validation is not satisfied
  const handleErrors = (event) => {
    const { name, value } = event.target;
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: value.trim() === '' ? 'Field is required' : '',
    }));
  };

  return (
    <div className="position-relative">
      {loading && (
        <div className="card-loader-overlay">
          <CircularProgress size={60} />
        </div>
      )}
    <div className="card-container">
      <div className="">
        <CardHeader
          title={
            <React.Fragment>
              <div className='card-head'>
                <div className="flex-head">
                  <img src="/assets/groupsvc.5fc77efe.jpg" alt="icon" className="card-icon" />
                  <label className='label'> Users <ArrowRightIcon  /> Add new user</label>
                </div>
                <div>
                  <Button className="back" onClick={()=> window.history.back()} variant="contained">Back</Button>
                </div>
              </div>
            </React.Fragment>
          }
        />
        <Divider className='divider' />
        <CardContent className="center-card">
          <Card className="user-card">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  className='errorTextField'
                  label="Name"
                  variant="outlined"
                  fullWidth
                  value={formData.name}
                  name="name"
                  onChange={handleInputChange}
                  onBlur={handleErrors}
                  error={!!formErrors.name}
                  helperText={formErrors.name}
                  required 
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  className='errorTextField'
                  label="User Name"
                  variant="outlined"
                  fullWidth
                  name='username'
                  value={formData.username}
                  onChange={handleInputChange}
                  onBlur={handleErrors}
                  error={!!formErrors.username}
                  helperText={formErrors.username}
                  required
                />
              </Grid>

              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Grid  className="grid" container spacing={4} alignItems="center">
                  <Grid item>
                    <PhoneInput
                      fullWidth
                      className="tellphone"
                      country={"india"}
                      enableSearch={true}
                      value={phone}
                      onChange={(value)=>setPhone(value)}
                    />
                  </Grid>
                  
                  <Grid item xs={6} sm={7} md={6} lg={8}>
                    <TextField 
                      className='errorTextField'
                      label="Phone Number"
                      variant="outlined"
                      fullWidth
                      name='mobile_digits'
                      value={formData.mobile_digits}
                      onChange={handleInputChange}
                      onBlur={handleErrors}
                      error={!!formErrors.mobile_digits}
                      helperText={formErrors.mobile_digits}
                      required
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <TextField
                  className='errorTextField'
                  label="Email ID"
                  variant="outlined"
                  fullWidth
                  name='email'
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleErrors}
                  error={!!formErrors.email}
                  helperText={formErrors.email}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                {companyNamesContainer && companyNamesContainer.results && (
                  <Autocomplete
                    options={companyNamesContainer.results || []}
                    getOptionLabel={(option) => option.name || ''}
                    value={companyNamesContainer.results.find((company) => company.id === selectedCompanyId) || null}
                    onChange={(e,value) => setSelectedCompanyId(value ? value.id : '')}
                    className='errorTextField'
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company"
                        variant="outlined"
                      />
                    )}
                  />
                )}
              </Grid>
            </Grid>
            <div className="radio-card">
              <FormLabel>Role</FormLabel>
              <RadioGroup
                row
                name="row-radio-buttons-group"
                onChange={(e) => setSelectedRoleId(e.target.value)}
                value={selectedRoleId}>
                {rolesDataContainer && rolesDataContainer.data ? (
                  rolesDataContainer.data.map((role) => {
                    if (companyNamesContainer&& companyNamesContainer.results && selectedCompanyId) {
                      const selectedCompany = companyNamesContainer.results.find(company => company.id === selectedCompanyId);
                      // This condition ensures to display all the roles for domain rhymtech.com
                      if (selectedCompany && selectedCompany.domain === "rhymtech.com") {
                        return (
                          <FormControlLabel
                            key={role.id}
                            value={role.id}
                            control={<Radio />}
                            label={role.label}
                            onChange={(e) => setSelectedRoleId(e.target.value)}
                          />
                        );
                      }
                    }
                    return (
                      <FormControlLabel
                        key={role.id}
                        value={role.id}
                        control={<Radio />}
                        label={role.label}
                        onChange={(e) => setSelectedRoleId(e.target.value)}
                      />);
                  })
                ) : null}
              </RadioGroup>
            </div>
            <div className="center-card radio-card">
              <Button className='blue-button' onClick={handleAddUser} variant="contained">Add new user</Button>
            </div>
            {errorMessage && <label className="error-message">{errorMessage}</label>}
          </Card>
        </CardContent>
      </div>
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle align="center">User created successfully</DialogTitle>
        <DialogContent className="form-container">
          <img src="/assets/tickmark.png" alt="img" className="card-icon" />
          <p>A new user account has been successfully created </p>
          <p>User ID: <b>{formData.name}</b></p>
          <p>Created At: {createdAt}</p>
          <p>An email with temporary password has been sent to <b>{formData.email}</b></p>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBackToAllUsers} className="card-button user-button">Back to all users</Button>
          <Button onClick={handleAddMoreUsers} variant="contained" className="blue-button">+ Add more users</Button>
        </DialogActions>
      </Dialog>
    </div>
   </div>
  );
}

export default AddUser;
